<template>
  <div class="radio">
    <input
      :id="id"
      type="radio"
      :checked="isChecked"
      :required="required"
      :value="value"
      :name="name"
      class="radio__field"
      @change="handleChange"
    />

    <label
      :for="id"
      :class="{ 'no-label': !label }"
      class="radio__label p-medium"
    >
      {{ label }} <sup v-if="required">*</sup>
    </label>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'

const props = defineProps({
  label: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    default: '',
  },
  checked: Boolean,
  modelValue: {
    type: [Array, String, Number, Boolean],
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
  value: {
    type: String,
    default: '',
  },
  name: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['update:modelValue'])

const isChecked = computed(() => {
  if (props.checked) {
    return props.checked
  }

  return props.modelValue === props.value
})

onMounted(() => {
  const urlParams = new URLSearchParams(window.location.search)
})

const handleChange = (e: Event) => {
  const { value } = e.target as HTMLInputElement

  emit('update:modelValue', value)
}
</script>

<style lang="scss" scoped>
.radio {
  display: flex;
  min-height: 3rem;
}

.radio__label {
  position: relative;
  display: flex;
  align-items: center;
  padding-left: 4.5rem;
  color: var(--c-foreground);

  &.no-label {
    width: 1.7rem;
    height: 1.7rem;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    width: 3rem;
    height: 3rem;
    border: 0.1rem solid var(--c-foreground);
    border-radius: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0.5rem;
    left: 0.5rem;
    width: 2rem;
    height: 2rem;
    background: var(--c-foreground);
    border-radius: 50%;
    transform: scale(0);
    transition: transform 0.2s;
  }
}

.radio__field {
  @extend %visually-hidden;

  border: 0;

  &:checked + .radio__label::after {
    transform: scale(1);
  }
}
</style>
